import { useState, useEffect } from "react";
import { 
  PaymentElement,
  useStripe, 
  useElements
} from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { ReactComponent as Spinner } from "../assets/images/spinner.svg";

const StripeCheckoutForm = ({ onPaymentComplete, amount, user, country, clientSecret, setShowPaymentMethod, appliedVoucher }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!stripe) return;

    const clientSecretPayment = new URLSearchParams(window.location.search).get( "payment_intent_client_secret");

    if (!clientSecretPayment) return;

    stripe.retrievePaymentIntent(clientSecretPayment).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setErrorMessage(null);
          onPaymentComplete();
          break;
        case "processing":
          setErrorMessage(t("Seu pagamento está sendo processado."));
          break;
        case "requires_payment_method":
          setErrorMessage(t("Seu pagamento anterior não foi bem-sucedido, tente novamente."));
          break;
        default:
          setErrorMessage(t("Algo deu errado."));
          break;
      }
    });
  }, [stripe, t, onPaymentComplete]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!stripe || !elements) return;

    setIsLoading(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-result`,
          receipt_email: user.email,
          payment_method_data: {
            billing_details: {
              name: user.name,
              email: user.email,
              address: {
                country: country || "BR"
              }
            }
          }
        },
        redirect: "if_required"
      });

      if (error) {
        if (error.type === "card_error" || error.type === "validation_error") {
          setErrorMessage(error.message);
        } else {
          setErrorMessage(t("Ocorreu um erro inesperado."));
        }
      } else {
        setErrorMessage(null);
        onPaymentComplete();
      }
    } catch (error) {
      console.error("Payment error:", error);
      setErrorMessage(t("Não foi possível processar o pagamento. Tente novamente mais tarde."));
    }

    setIsLoading(false);
  };

  const listCurrencyCountry = {
    "usd": { locale: "en-US", currency: "USD" },
    "brl": { locale: "pt-BR", currency: "BRL" },
    "eur": { locale: "de-DE", currency: "EUR" }
  }
  
  // Função para formatar valor monetário corretamente
  const formatCurrency = (value, currencyCode) => {
    if (value === undefined || value === null || !currencyCode) return "";
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "";
    
    const config = listCurrencyCountry[currencyCode] || listCurrencyCountry["usd"];
    
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.currency
    }).format(numValue);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <h3>{t("Summary")}</h3>
      
      <div className="payment-summary">
        <div className="payment-row">
          <div className="payment-type">{t("Expert Validation")}</div>
          <div className="payment-amount">{formatCurrency(amount?.amount, amount?.currency)}</div>
        </div>

        { amount?.isValid && (<div className="payment-row">
          <div className="payment-type" style={{ color: 'red' }}>{amount?.voucher}</div>
          <div className="payment-amount"  style={{ color: 'red' }}>- {formatCurrency(amount?.amountDiscounted, amount?.currency)}</div>
        </div>)}
        
        <div className="payment-row total">
          <div className="payment-type">{t("TOTAL")}</div>
          <div className="payment-amount">{formatCurrency(amount?.amountTotal, amount?.currency)}</div>
        </div>
      </div>

      <PaymentElement id="payment-element" options={{
        fields: {
        },
        defaultValues: {
          billingDetails: {
            name: `${user.name} ${user.lastName}`,
            email: user.email,
            phone: user.mobileNumber
          }
        },
        paymentMethodOrder: ['card', 'google_pay']
      }} />
      
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      
      <div className="stripe-powered">
        <img src="/images/stripe_logo.png" alt="Powered by Stripe" className="stripe-logo" />
      </div>

      <div className="action-buttons">
      <button 
          onClick={() => setShowPaymentMethod(false)}
          className="back-button"
        >
          {t("Back")}
        </button>
        <button 
          disabled={isLoading || !stripe || !elements} 
          id="submit" 
          className="continue-button"
        >
          {isLoading ? <Spinner /> : t("Checkout")}
        </button>
      </div>
    </form>
  );
};

export default StripeCheckoutForm;